import {
  ArrowRight,
  Calendar,
  CalendarDays,
  Filter,
  RefreshCw,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Badge, Button } from "reactstrap";
import { useGenerateAllocationPlansMutation } from "../../redux/services/allocationService";
import { isDateInPast } from "../../utility/Utility";
import CartAllocation from "./AllocationSystem/CartAllocation";
import { CycleBreakdownScreen } from "./AllocationSystem/CycleBreakdown";
import "./index.css";
import { RealtimeInventoryDashboard } from "./RealTimeInventoryDashboard";
import { ApprovalsTab } from "./AllocationSystem/Approvals/ApprovalTab";
import { ProductionPlanningScreen } from "./AllocationSystem/ProductionPlanning";
import PacksTab from "./AllocationSystem/Packs";
import ChannelBreakDown from "./AllocationSystem/ChannelBreakdown";
// import ChannelBreakDown from "./AllocationSystem/ChannelBreakDown";

const OperationsDashboard = () => (
  <div className="dashboard-placeholder">
    <div className="placeholder-content">
      <h2>Operations Dashboard</h2>
      <p>Real-time production monitoring and KPI tracking coming soon...</p>
    </div>
  </div>
);

const CartCycleAllocationView = () => (
  <div className="dashboard-placeholder">
    <div className="placeholder-content">
      <h2>Cart-Cycle Allocation Matrix</h2>
      <p>
        Interactive matrix view showing cart allocations across production
        cycles
      </p>
    </div>
  </div>
);

const ProductionCartTimeline = () => (
  <div className="dashboard-placeholder">
    <div className="placeholder-content">
      <h2>Production & Cart Timeline</h2>
      <p>
        Visual timeline showing production cycles and cart deployment schedules
      </p>
    </div>
  </div>
);

const WebOrderingScreen = () => (
  <div className="dashboard-placeholder">
    <div className="placeholder-content">
      <h2>Web Order Management</h2>
      <p>Manage online orders, track order status, and assign to carts</p>
    </div>
  </div>
);

const Tabs = ({
  value,
  onValueChange,
  children,
}: {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
}) => <div className="tabs">{children}</div>;

const TabsList = ({ children }: { children: React.ReactNode }) => (
  <div className="tabs-list">{children}</div>
);

const TabsTrigger = ({
  value,
  children,
  className = "",
  activeTab,
  onClick,
}: {
  value: string;
  children: React.ReactNode;
  className?: string;
  activeTab: string;
  onClick?: () => void;
}) => {
  const isActive = activeTab === value;
  return (
    <button
      className={`tabs-trigger ${className} ${isActive ? "active" : ""}`}
      onClick={onClick}
      data-active={isActive}
    >
      {children}
    </button>
  );
};

const TabsContent = ({
  value,
  children,
  activeTab,
}: {
  value: string;
  children: React.ReactNode;
  activeTab: string;
}) => {
  if (value !== activeTab) return null;
  return <div className="tabs-content">{children}</div>;
};

// ── DateRangeFilter ──────────────────────────────────────────────────────────

interface DateRangeFilterProps {
  restaurantId: string;
  onFilterApplied?: (result: any) => void;
  onDatesChange?: (loadedFrom: string, savedTo: string) => void;
  onLoadFromChange?: (loadedFrom: string) => void;
  isVisible?: boolean;
  initialLoadedFrom?: string;
  initialSavedTo?: string;
}

const DateRangeFilter = ({
  restaurantId,
  onFilterApplied,
  onDatesChange,
  onLoadFromChange,
  isVisible = true,
  initialLoadedFrom,
  initialSavedTo,
}: DateRangeFilterProps) => {
  const [generateAllocationPlans, { isLoading }] =
    useGenerateAllocationPlansMutation();

  const getCurrentDate = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  };

  const [loadedFrom, setLoadedFrom] = useState(
    initialLoadedFrom || getCurrentDate(),
  );
  const [savedTo, setSavedTo] = useState(initialSavedTo || getCurrentDate());

  useEffect(() => {
    if (initialLoadedFrom && initialLoadedFrom !== loadedFrom)
      setLoadedFrom(initialLoadedFrom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoadedFrom]);

  useEffect(() => {
    if (initialSavedTo && initialSavedTo !== savedTo)
      setSavedTo(initialSavedTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSavedTo]);

  const handleDateChange = (type: "loadedFrom" | "savedTo", value: string) => {
    if (type === "loadedFrom") {
      setLoadedFrom(value);
      if (onLoadFromChange) onLoadFromChange(value);
    } else {
      setSavedTo(value);
    }
    if (onDatesChange) {
      onDatesChange(
        type === "loadedFrom" ? value : loadedFrom,
        type === "savedTo" ? value : savedTo,
      );
    }
  };

  const handleFilter = async () => {
    if (!restaurantId) {
      toast.error("Restaurant ID is required");
      return;
    }
    try {
      const fmt = (d: string, h: string, m: string, s: string) =>
        `${d}T${h}:${m}:${s}Z`;
      const result = await generateAllocationPlans({
        restaurantId,
        loadFrom: fmt(loadedFrom, "00", "00", "00"),
        saveTo: fmt(savedTo, "00", "00", "00"),
      }).unwrap();
      toast.success(
        loadedFrom === savedTo
          ? `Data loaded for ${loadedFrom}`
          : `Template generated! ${loadedFrom} → ${savedTo}`,
      );
      if (onFilterApplied) onFilterApplied(result);
    } catch (err: any) {
      toast.error(
        loadedFrom === savedTo
          ? `No data found for ${loadedFrom}`
          : "Production Cycle or Cycle Breakdown not found for given dates",
      );
    }
  };

  if (!isVisible) return null;

  return (
    <div className="date-range-filter">
      <div className="date-range-container">
        <div className="date-input-group">
          <div className="date-input-wrapper">
            <label className="date-label">
              <Calendar size={14} className="me-1" />
              Load From
            </label>
            <input
              type="date"
              className="date-input"
              value={loadedFrom}
              onChange={(e) => handleDateChange("loadedFrom", e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className="date-arrow">
          <ArrowRight size={16} />
        </div>
        <div className="date-input-group">
          <div className="date-input-wrapper">
            <label className="date-label">
              <CalendarDays size={14} className="me-1" />
              Save To
            </label>
            <input
              type="date"
              className="date-input"
              value={savedTo}
              onChange={(e) => handleDateChange("savedTo", e.target.value)}
              disabled={isLoading}
              min={loadedFrom}
            />
          </div>
        </div>
        {loadedFrom !== savedTo && (
          <button
            className={`filter-button ${isLoading ? "loading" : ""}`}
            onClick={handleFilter}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner" />
                Generating...
              </>
            ) : (
              <>
                <Filter size={18} />
                <span>Generate</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// ── Allocation ────────────────────────────────────────────────────────

export function Allocation() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("realtime-inventory");
  const [productionQuantities, setProductionQuantities] = useState<
    Record<string, number>
  >({});
  const [packQuantities, setPackQuantities] = useState<Record<string, number>>(
    {},
  );
  const [isPastDate, setIsPastDate] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [pendingCount, setPendingCount] = useState(0);

  // Handle navigation state to set active tab
  useEffect(() => {
    // Check if we have state from navigation
    if (location.state && (location.state as any).activeTab) {
      const targetTab = (location.state as any).activeTab;
      setActiveTab(targetTab);
      
      // Optional: Clear the state after using it to prevent re-applying on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const getCurrentDate = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  };

  const [templateLoadedFrom, setTemplateLoadedFrom] =
    useState(getCurrentDate());
  const [templateSavedTo, setTemplateSavedTo] = useState(getCurrentDate());
  const [isUsingTemplate, setIsUsingTemplate] = useState(false);
  const [isSingleDateMode, setIsSingleDateMode] = useState(false);
  const [componentDisplayDates, setComponentDisplayDates] = useState<{
    [tab: string]: string;
  }>({
    "production-planning": getCurrentDate(),
    "cycle-breakdown": getCurrentDate(),
    "master-allocation": getCurrentDate(),
    "channel-breakdown": getCurrentDate(),
  });
  const [templateGenerated, setTemplateGenerated] = useState(false);

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id,
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getWorkflowStep = (tab: string): number => {
    const stepMap: Record<string, number> = {
      "production-planning": 1,
      "cycle-breakdown": 2,
      "master-allocation": 3,
      "channel-breakdown": 4,
      "cart-cycle-view": 5,
      timeline: 6,
      "web-ordering": 7,
    };
    return stepMap[tab] || 0;
  };

  const handleFilterApplied = (result: any) => {
    const isTemplate = templateLoadedFrom !== templateSavedTo;
    setIsUsingTemplate(isTemplate);
    setIsSingleDateMode(!isTemplate && templateLoadedFrom !== getCurrentDate());
    setTemplateGenerated(true);
    if (isTemplate) {
      setComponentDisplayDates({
        "production-planning": templateSavedTo,
        "cycle-breakdown": templateSavedTo,
        "master-allocation": templateSavedTo,
        "channel-breakdown": templateSavedTo,
      });
    } else {
      const dateToShow = isSingleDateMode
        ? templateLoadedFrom
        : getCurrentDate();
      setComponentDisplayDates({
        "production-planning": dateToShow,
        "cycle-breakdown": dateToShow,
        "master-allocation": dateToShow,
        "channel-breakdown": dateToShow,
      });
      if (isSingleDateMode)
        toast.success(
          `Data loaded for ${templateLoadedFrom}. All tabs will show this date.`,
        );
    }
    setActiveTab("production-planning");
  };

  useEffect(() => {
    if (isSingleDateMode && templateLoadedFrom) {
      setIsPastDate(isDateInPast(templateLoadedFrom));
    } else {
      setIsPastDate(false);
    }
  }, [isSingleDateMode, templateLoadedFrom]);

  const handleDatesChange = (loadedFrom: string, savedTo: string) => {
    setTemplateLoadedFrom(loadedFrom);
    setTemplateSavedTo(savedTo);
    const isTemplate = loadedFrom !== savedTo;
    setIsUsingTemplate(isTemplate);
    setIsSingleDateMode(!isTemplate && loadedFrom !== getCurrentDate());
    setTemplateGenerated(false);
    if (isTemplate) {
      setComponentDisplayDates((prev) => ({
        ...prev,
        [activeTab]: loadedFrom,
      }));
    } else {
      setComponentDisplayDates({
        "production-planning": loadedFrom,
        "cycle-breakdown": loadedFrom,
        "master-allocation": loadedFrom,
        "channel-breakdown": loadedFrom,
      });
    }
  };

  useEffect(() => {
    const currentDate = getCurrentDate();
    setTemplateLoadedFrom(currentDate);
    setTemplateSavedTo(currentDate);
    setComponentDisplayDates({
      "production-planning": currentDate,
      "cycle-breakdown": currentDate,
      "master-allocation": currentDate,
      "channel-breakdown": currentDate,
    });
  }, []);

  useEffect(() => {
    if (!templateGenerated) {
      const base =
        isSingleDateMode || isUsingTemplate
          ? templateLoadedFrom
          : getCurrentDate();
      setComponentDisplayDates({
        "production-planning": base,
        "cycle-breakdown": base,
        "master-allocation": base,
        "channel-breakdown": base,
      });
    }
  }, [
    isUsingTemplate,
    isSingleDateMode,
    templateGenerated,
    templateLoadedFrom,
  ]);

  const handleLoadFromChange = (loadedFrom: string) => {
    setTemplateLoadedFrom(loadedFrom);
    if (loadedFrom === templateSavedTo) {
      setIsUsingTemplate(false);
      setIsSingleDateMode(loadedFrom !== getCurrentDate());
      setTemplateGenerated(false);
    }
    setComponentDisplayDates({
      "production-planning": loadedFrom,
      "cycle-breakdown": loadedFrom,
      "master-allocation": loadedFrom,
      "channel-breakdown": loadedFrom,
    });
  };

  const handleTabChange = (tab: string) => setActiveTab(tab);

  const resetToCurrentDate = () => {
    const currentDate = getCurrentDate();
    setTemplateLoadedFrom(currentDate);
    setTemplateSavedTo(currentDate);
    setIsUsingTemplate(false);
    setIsSingleDateMode(false);
    setTemplateGenerated(false);
    setComponentDisplayDates({
      "production-planning": currentDate,
      "cycle-breakdown": currentDate,
      "master-allocation": currentDate,
      "channel-breakdown": currentDate,
    });
    handleDatesChange(currentDate, currentDate);
    handleLoadFromChange(currentDate);
    toast.info("Reset to current date");
  };

  const shouldShowDateRangeFilter = () => {
    const viewOnlyTabs = [
      "dashboard",
      "realtime-inventory",
      "approvals",
      "packs",
    ];
    return !viewOnlyTabs.includes(activeTab);
  };

  const shouldShowResetButton = () =>
    (isSingleDateMode || templateLoadedFrom !== getCurrentDate()) &&
    shouldShowDateRangeFilter();

  return (
    <div className="allocation-workflow">
      {/* Header */}
      <header className="workflow-header">
        <div className="header-content">
          <div className="header-left">
            <h1>
              {activeTab === "dashboard"
                ? "Operations Dashboard"
                : activeTab === "realtime-inventory"
                  ? "Real-time Inventory"
                  : activeTab === "approvals"
                    ? "Request Approvals"
                    : activeTab === "packs"
                      ? "Packs"
                      : "Allocation System"}
            </h1>

            {activeTab !== "dashboard" &&
              activeTab !== "realtime-inventory" &&
              activeTab !== "approvals" &&
              activeTab !== "packs" && (
                <>
                  {isUsingTemplate ? (
                    <div className="template-indicator">
                      <Badge color="info" className="me-2">
                        <Filter size={12} className="me-1" />
                        Template Mode
                        {templateGenerated ? " (Generated)" : " (Setting up)"}
                      </Badge>
                      <span className="template-dates">
                        <Calendar size={12} className="me-1" />
                        Loaded from: <strong>{templateLoadedFrom}</strong>
                        <span className="mx-2">→</span>
                        <CalendarDays size={12} className="me-1" />
                        Save to: <strong>{templateSavedTo}</strong>
                      </span>
                      {shouldShowResetButton() && (
                        <Button
                          color="link"
                          size="sm"
                          className="ms-2 p-0"
                          onClick={resetToCurrentDate}
                        >
                          <RefreshCw size={12} />
                        </Button>
                      )}
                      <span className="ms-3 text-info">
                        <small>
                          {templateGenerated
                            ? `Showing ${templateSavedTo} data in all tabs`
                            : `Setting up template from ${templateLoadedFrom} to ${templateSavedTo}`}
                        </small>
                      </span>
                    </div>
                  ) : isSingleDateMode ? (
                    <div className="single-date-indicator">
                      <Badge color="warning" className="me-2">
                        <Calendar size={12} className="me-1" />
                        Viewing Date
                      </Badge>
                      <span>
                        Showing data for: <strong>{templateLoadedFrom}</strong>
                      </span>
                      <Button
                        color="link"
                        size="sm"
                        className="ms-2 p-0"
                        onClick={resetToCurrentDate}
                      >
                        <RefreshCw size={12} />
                      </Button>
                    </div>
                  ) : (
                    shouldShowDateRangeFilter() && (
                      <div className="current-date-indicator">
                        <Badge color="primary" className="me-2">
                          <Calendar size={12} className="me-1" />
                          Current Date
                        </Badge>
                        <span>
                          Showing data for: <strong>{getCurrentDate()}</strong>
                        </span>
                      </div>
                    )
                  )}
                </>
              )}
          </div>

          <div className="header-right">
            <DateRangeFilter
              restaurantId={restaurantId}
              onFilterApplied={handleFilterApplied}
              onDatesChange={handleDatesChange}
              onLoadFromChange={handleLoadFromChange}
              isVisible={shouldShowDateRangeFilter()}
              initialLoadedFrom={templateLoadedFrom}
              initialSavedTo={templateSavedTo}
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="workflow-content">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger
              value="dashboard"
              className="workflow-tab"
              activeTab={activeTab}
              onClick={() => handleTabChange("dashboard")}
            >
              Dashboard
            </TabsTrigger>
            <TabsTrigger
              value="realtime-inventory"
              className="workflow-tab"
              activeTab={activeTab}
              onClick={() => handleTabChange("realtime-inventory")}
            >
              Real-time Inventory
            </TabsTrigger>

            <div className="tab-separator" />

            <TabsTrigger
              value="approvals"
              className="workflow-tab"
              activeTab={activeTab}
              onClick={() => handleTabChange("approvals")}
            >
              Approvals
              {pendingCount > 0 && (
                <Badge color="danger" className="ms-1" size="sm">
                  {pendingCount}
                </Badge>
              )}
            </TabsTrigger>

            <TabsTrigger
              value="packs"
              className="workflow-tab"
              activeTab={activeTab}
              onClick={() => handleTabChange("packs")}
            >
              Packs
            </TabsTrigger>

            <TabsTrigger
              value="production-planning"
              className="workflow-tab"
              activeTab={activeTab}
              onClick={() => handleTabChange("production-planning")}
            >
              Production Planning
              {isUsingTemplate && (
                <Badge color="info" className="ms-1" size="sm">
                  {componentDisplayDates["production-planning"]}
                </Badge>
              )}
            </TabsTrigger>

            <TabsTrigger
              value="cycle-breakdown"
              className="workflow-tab"
              activeTab={activeTab}
              onClick={() => handleTabChange("cycle-breakdown")}
            >
              Cycle Breakdown
              {isUsingTemplate && (
                <Badge color="info" className="ms-1" size="sm">
                  {componentDisplayDates["cycle-breakdown"]}
                </Badge>
              )}
            </TabsTrigger>

            <TabsTrigger
              value="master-allocation"
              className="workflow-tab"
              activeTab={activeTab}
              onClick={() => handleTabChange("master-allocation")}
            >
              Cart Allocation
              {isUsingTemplate && (
                <Badge color="info" className="ms-1" size="sm">
                  {componentDisplayDates["master-allocation"]}
                </Badge>
              )}
            </TabsTrigger>

            <TabsTrigger
              value="channel-breakdown"
              className="workflow-tab"
              activeTab={activeTab}
              onClick={() => handleTabChange("channel-breakdown")}
            >
              Channel Breakdown
              {isUsingTemplate && (
                <Badge color="info" className="ms-1" size="sm">
                  {componentDisplayDates["channel-breakdown"]}
                </Badge>
              )}
            </TabsTrigger>

            <TabsTrigger
              value="cart-cycle-view"
              className="workflow-tab"
              activeTab={activeTab}
              onClick={() => handleTabChange("cart-cycle-view")}
            >
              Cart-Cycle View
            </TabsTrigger>
            <TabsTrigger
              value="timeline"
              className="workflow-tab"
              activeTab={activeTab}
              onClick={() => handleTabChange("timeline")}
            >
              Timeline
            </TabsTrigger>

            <div className="tab-separator" />

            <TabsTrigger
              value="web-ordering"
              className="workflow-tab"
              activeTab={activeTab}
              onClick={() => handleTabChange("web-ordering")}
            >
              Order Management
            </TabsTrigger>
          </TabsList>

          {/* Tab Contents */}
          <TabsContent value="dashboard" activeTab={activeTab}>
            <OperationsDashboard />
          </TabsContent>
          <TabsContent value="realtime-inventory" activeTab={activeTab}>
            <RealtimeInventoryDashboard />
          </TabsContent>
          <TabsContent value="approvals" activeTab={activeTab}>
            <ApprovalsTab />
          </TabsContent>

          <TabsContent value="packs" activeTab={activeTab}>
            <PacksTab
              quantities={packQuantities}
              onQuantitiesChange={setPackQuantities}
              selectedDate={templateLoadedFrom}
            />
          </TabsContent>

          <TabsContent value="production-planning" activeTab={activeTab}>
            <ProductionPlanningScreen
              key={`production-planning-${componentDisplayDates["production-planning"]}`}
              quantities={productionQuantities}
              setQuantities={setProductionQuantities}
              selectedDate={componentDisplayDates["production-planning"]}
              setSelectedDate={() => {}}
              templateMode={isUsingTemplate}
              singleDateMode={isSingleDateMode}
              templateSourceDate={
                isUsingTemplate ? templateLoadedFrom : undefined
              }
              templateSaveToDate={
                isUsingTemplate
                  ? templateSavedTo
                  : isSingleDateMode
                    ? templateLoadedFrom
                    : undefined
              }
              isShowingLoadFromDate={
                componentDisplayDates["production-planning"] ===
                templateLoadedFrom
              }
              isPastDate={isPastDate}
            />
          </TabsContent>
          <TabsContent value="cycle-breakdown" activeTab={activeTab}>
            <CycleBreakdownScreen
              productionQuantities={productionQuantities}
              selectedDate={componentDisplayDates["cycle-breakdown"]}
              templateMode={isUsingTemplate}
              singleDateMode={isSingleDateMode}
              templateSourceDate={
                isUsingTemplate ? templateLoadedFrom : undefined
              }
              templateSaveToDate={
                isUsingTemplate
                  ? templateSavedTo
                  : isSingleDateMode
                    ? templateLoadedFrom
                    : undefined
              }
              isShowingLoadFromDate={
                componentDisplayDates["cycle-breakdown"] === templateLoadedFrom
              }
              isPastDate={isPastDate}
            />
          </TabsContent>
          <TabsContent value="master-allocation" activeTab={activeTab}>
            <CartAllocation
              productionQuantities={productionQuantities}
              selectedDate={componentDisplayDates["master-allocation"]}
              templateMode={isUsingTemplate}
              singleDateMode={isSingleDateMode}
              templateSourceDate={
                isUsingTemplate ? templateLoadedFrom : undefined
              }
              templateSaveToDate={
                isUsingTemplate
                  ? templateSavedTo
                  : isSingleDateMode
                    ? templateLoadedFrom
                    : undefined
              }
              isShowingLoadFromDate={
                componentDisplayDates["master-allocation"] ===
                templateLoadedFrom
              }
              isPastDate={isPastDate}
            />
          </TabsContent>
          <TabsContent value="channel-breakdown" activeTab={activeTab}>
            <ChannelBreakDown
              productionQuantities={productionQuantities}
              selectedDate={componentDisplayDates["channel-breakdown"]}
              templateMode={isUsingTemplate}
              singleDateMode={isSingleDateMode}
              templateSourceDate={
                isUsingTemplate ? templateLoadedFrom : undefined
              }
              templateSaveToDate={
                isUsingTemplate
                  ? templateSavedTo
                  : isSingleDateMode
                    ? templateLoadedFrom
                    : undefined
              }
              isShowingLoadFromDate={
                componentDisplayDates["channel-breakdown"] ===
                templateLoadedFrom
              }
              isPastDate={isPastDate}
            />
          </TabsContent>
          <TabsContent value="cart-cycle-view" activeTab={activeTab}>
            <CartCycleAllocationView />
          </TabsContent>
          <TabsContent value="timeline" activeTab={activeTab}>
            <ProductionCartTimeline />
          </TabsContent>
          <TabsContent value="web-ordering" activeTab={activeTab}>
            <WebOrderingScreen />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}